/*global define*/
/*jshint devel:true*/
/**
 * Main Rivets configuration and subscribe/unsubscribe/read/publish handlers.
 * Designed to closely integrate with Backbone classes.
 */
define(
	'setupRivets',[
		'backbone',
		'rivets',
		'jquery',
		'moment',
		'underscoreToBoolean',
		'momentTimezoneWithData'
	],
	function (Backbone, rivets, $, moment, _, momentTimezoneWithData) {
		'use strict';

		var BackboneCollection = Backbone.Collection,
			BackboneModel = Backbone.Model;

		rivets.configure({

			prefix: 'rv',

			// Setup adapters to interface with backbone events
			adapter: {
				subscribe: function (obj, keypath, callback) {
					//DEBUG
					//console.log('setup rivets: subscribe: keypath: ' + keypath, ', obj = ', obj);

					if (!obj) { // if the object is invalid, don't subscribe. It must be due to a data-rv-* attribute
								// used for non-rivet stuff.
						return;
					}

					if (obj instanceof BackboneCollection) {

						//DEBUG
						//console.log('root obj is a collection');

						obj.on('add remove reset', callback);
					} else if (obj instanceof BackboneModel) {
						///////
						var directSubscribe = function (obj, directKeyPath) {
							var targetVar = obj.get(directKeyPath);

							// handle collections nested in models
							if (targetVar instanceof BackboneCollection) {

								//DEBUG
								//console.log('root obj[%o] is a Collection', keypath);

								targetVar.on('add remove reset', callback);
							} else {
								//DEBUG
								//console.log('root obj is a Backbone model', keypath);

								// listen to attribute changes
								obj.on('change:' + directKeyPath, callback);

								// if the target attribute is a nested backbone model, listen to its internal changes too
								if (targetVar instanceof BackboneModel) {
									//DEBUG
									//console.log('targetVar obj is a Backbone model', keypath);
									targetVar.on('change', callback);
								}
							}
						};
						///////

						// simple class check
						if (!obj.nestedGetModel || !obj.nestedGetModel.apply) {
							throw new Error('rivets:adapter.subscribe(): use a model that extends from BaseModel ' +
											'instead of just Backbone.Model.');
						}

						var directModel = obj.nestedGetModel(keypath);
						directSubscribe(directModel.model, directModel.keypath);

					} else {
						throw new Error('rivets:adapter.subscribe(): Unsupported object type.');
					}
				},
				unsubscribe: function (obj, keypath, callback) {
					//DEBUG
					//console.log('setup rivets: unsubscribe: keypath: ' + keypath, ', obj = ', obj);

					if (!obj) { // if the object is invalid, don't subscribe. It must be due to a data-rv-* attribute
								// used for non-rivet stuff.
						return;
					}

					if (obj instanceof BackboneCollection) {

						//DEBUG
						//console.log('root obj is a collection');

						obj.off('add remove reset', callback);
					} else if (obj instanceof BackboneModel) {

						// simple class check
						if (!obj.nestedGetModel || !obj.nestedGetModel.apply) {
							throw new Error('rivets:adapter.unsubscribe(): use a model that extends from BaseModel ' +
											'instead of just Backbone.Model.');
						}

						// find the direct model in case the keypath is nested
						var directModelInfo = obj.nestedGetModel(keypath);
						keypath = directModelInfo.keypath;
						obj = directModelInfo.model;

						var targetVar = obj.get(keypath);

						// handle collections nested in models
						if (targetVar instanceof BackboneCollection) {

							//DEBUG
							//console.log('root obj[%o] is a Collection', keypath);

							targetVar.off('add remove reset', callback);
						} else {
							//DEBUG
							//console.log('root obj is a Backbone model', keypath);

							obj.off('change:' + keypath, callback);

							// if the target attribute is a nested backbone model
							if (targetVar instanceof BackboneModel) {
								//DEBUG
								//console.log('targetVar obj is a Backbone model', keypath);
								targetVar.off('change', callback);
							}
						}
					} else {
						throw new Error('Unsupported object type.');
					}
				},
				read: function (obj, keypath) {
					var ret;

					if (!obj) { // if the object is invalid, return undefined. It must be due to a data-rv-* attribute
								// used for non-rivet stuff.
						return;
					}

					if (obj instanceof BackboneCollection) {
						ret = obj.models;
					} else if (obj instanceof BackboneModel) {
						// simple class check
						if (!obj.nestedGetModel || !obj.nestedGetModel.apply) {
							throw new Error('rivets:adapter.read(): use a model that extends from BaseModel instead ' +
											'of just Backbone.Model.');
						}

						var targetVar = obj.nestedGet(keypath);

						if (targetVar instanceof BackboneCollection) {
							ret = targetVar.models;
						} else {
							ret = targetVar;
						}
					} else {
						ret = obj[keypath];
					}

					return ret;
				},
				publish: function (obj, keypath, value) {
					//DEBUG
					//console.group('rivets publish');
					//console.log('rivets->adapter.publish:', [].slice.call(arguments));

					if (!obj) { // if the object is invalid, do nothing. It must be due to a data-rv-* attribute
								// used for non-rivet stuff.
						return;
					}

					var isBackboneModel = obj instanceof BackboneModel;

					// if it's a backbone model or collection
					if (isBackboneModel || (obj instanceof BackboneCollection)) {

						if (isBackboneModel) { // for backbone models
							// simple class check
							if (!obj.nestedGetModel || !obj.nestedGetModel.apply) {
								throw new Error('rivets:adapter.publish(): use a model that extends from BaseModel ' +
												'instead of just Backbone.Model.');
							}

							// find the direct model in case the keypath is nested
							var directModelInfo = obj.nestedGetModel(keypath);
							keypath = directModelInfo.keypath;
							obj = directModelInfo.model;
						}

						obj.set(keypath, value);
					} else {
						obj[keypath] = value;
					}

					//DEBUG
					//console.groupEnd();
				}
			}
		});

		// extend rivets.formatters
		$.extend(
			rivets.formatters,
			{
				trueFalse: function (value) {
					return ( value === true || (value + '').toLocaleLowerCase() === 'true' ) ? 'On' : 'Off';
				},
				/**
				 * Converts value to a boolean.
				 *
				 * @param {mixed} value
				 * @return {boolean} Returns true if value is true, 'true', '1' or a non-empty object.
				 * Returns false otherwise.
				 */
				toBoolean: function (value) {
					return _.toBoolean(value);
				},
				date: function (value) {
					return moment(value).format('MMM DD, YYYY');
				},
				/**
				 * Converts value to upper case string
				 * @param value
				 * @return {String}
				 */
				toUpperCase: function (value) {
					return (value + '').toUpperCase();
				},
				/**
				 * Converts value to lower case string
				 * @param value
				 * @return {String}
				 */
				toLowerCase: function (value) {
					return (value + '').toLowerCase();
				},
				json: {
					/**
					 * Converts value to a JSON string
					 * @param {*} value
					 * @returns {String}
					 */
					read: function (value) {
						return JSON.stringify(value === undefined ? null : value);
					},
					/**
					 * Converts JSON string to a javascript Object
					 * @param {String} value
					 * @returns {Object}
					 */
					publish: function (value) {
						return JSON.parse(value);
					}
				},
				/**
				 * Debug-helper
				 * @param {*} value
				 * @returns {*}
				 */
				consoleLog: function (value) {
					console.log('rivets: consoleLog: this = ', this, ', arguments = ', arguments);
					console.log('rivets: consoleLog: stringify = ', JSON.stringify(value));
					console.log('rivets: consoleLog: value = ', value);
					return value;
				},
				/**
				 * Returns the opposite boolean value of the given value
				 * @param value
				 * @return {boolean}
				 */
				not: function (value) {
					return !value;
				},
				/**
				 * Compares the given value with a given argument
				 * @param {String} value
				 * @param {String} comparedValue
				 * @returns {boolean}
				 */
				strEqual: function (value, comparedValue) {
					return (value + '') === comparedValue;
				},

				strNotEqual: function (value, comparedValue) {
					return (value + '') !== comparedValue;
				},

				/**
				 * Append a string
				 * @param {String} value
				 * @param {String} appendValue
				 * @returns {boolean} updated value
				 */
				append: function (value, appendValue) {
					return (value + ' ' + appendValue);
				},

				/**
				 * Used for rv-show.
				 *
				 * Example:
				 *
				 * <p data-rv-show="module.changeRatePlanContainerUrl | hasContent">
				 *     Would you like to <a data-rv-href="module.changeRatePlanContainerUrl">change your plan?</a>
				 * </p>
				 *
				 * @see http://freemarker.org/docs/ref_builtins_expert.html#ref_builtin_has_content
				 *
				 * @param value
				 * @returns {boolean} If value is defined and not "empty", return true. Else false.
				 */
				hasContent: function (value) {

					if (typeof value === 'undefined') {
						return false;
					}

					// object or array
					if (typeof value === 'object') {
						return !$.isEmptyObject(value);
					}

					if (typeof value === 'string') {
						return $.trim(value) !== '';
					}

					// Default = no content
					return false;

				},

				/**
				 * Currently for usage summary, previous and details can be used where there is  need to convert
				 * from bytes to MB or GB.
				 * @param {String} value
				 * @param {String} showUnitAndGB - if true convert GB for more than 1000 and show unit MB or GB.
				 * If false always show MB value.
				 */
				byteConversion: function (value, showUnitAndGB) {
					var convertedValue = null,
						gb = 1048576000,
						mb = 1048576;

					if (showUnitAndGB){
						if (+(value) >= gb) {
							convertedValue = parseFloat((+(value) / gb).toFixed(2)) + ' GB';
						} else {
							convertedValue = parseFloat((+(value) / mb).toFixed(2)) + ' MB';
						}
					} else {
						convertedValue = parseFloat((+(value) / mb).toFixed(2));
					}

					return convertedValue;
				},

				/**
				 * converting values into cents
				 */
				centConversion: function (value, upscale) {
					var outputStr = '$' + (value / 100).toFixed(2);
					return outputStr;
				},

				/**
				 * converting values into cents, hide cents part if it's .00
				 */
				smartCentConversion: function (value, upscale) {
					var outputStr = null;

					if (value % 100 !== 0) {
						outputStr = '$' + (value / 100).toFixed(2);
					} else {
						outputStr = '$' + (value / 100);
					}

					return outputStr;
				},
				/**
				 * Formatting the date to DD/MM/YYYY
				 */
				dateFormat:{

					/**
					 * Converts value to a date string
					 * @param {*} value
					 * @returns {String}
					 */
					read: function (value, format) {

                        // as Rivets doesn't support space symbol in parameters
                        // have to use underscore symbol as a replacement for space
                        format = format.replace(/_/gi,' ');

						if (value){
							//DEBUG
							//console.log("value read::"+value);
							var newVal = moment.tz(value, 'Australia/Sydney').format(format);
							value = newVal;

						}
						return value;
					},
					/**
					 * Converts date to a javascript Object
					 * @param {String} value
					 * @returns {Date}
					 */
					publish: function (value, format) {

						if (value){
							var newVal = (moment(value, [format,'DD-MM-YYYY']).toDate());
							var newEpochVal = (moment(newVal).unix())*1000;
							value = newEpochVal;
						}

						return value;
					}
				},
				/**
				 * Returns a property of the current object
				 * @param {Object} obj
				 * @param {string} propertyName
				 * @returns {*}
				 */
                getProperty: function (obj, propertyName) {
                    return obj[propertyName]; // will break if obj is undefined, but it's your fault, not mine ;-)
                },

                length: function (obj) {
                    return obj.length;
                },

				toAmount : function (value) {
                    if(!isNaN(parseFloat(value)) && isFinite(value)) {
                        return parseFloat(value).toFixed(2);
                    } else {
                        return value;
                    }
                }
			}
		);

		// extend rivets.binders
		$.extend(
			rivets.binders,
			{
				backgroundimg: function (element, v) {
					return $(element).css({
						'background-image': 'url("' + v + '")'
					});
				},
				width: function (element, v) {
					return $(element).css({
						'width': v + '%'
					});
				},
				link: function (element, v) {
					return $(element).attr('href', v);
				},
				'add-class': function (element, v) {
					return $(element).addClass(v);
				},
				'remove-class': function (element, v) {
					return $(element).removeClass(v);
				}
			}
		);

		return rivets;
	}
);

